import React from 'react';
import {Box, CircularProgress, Fade, Stack, Typography} from '@mui/material';

const CIRCLE_SIZE = 88;
const CIRCLE_THICKNESS = 3;

/**
 * Center-pane circular progress — shown while the pipeline is running.
 * Shows a determinate ring when percent is known, indeterminate otherwise.
 * When `status` transitions to null / non-processing the caller should unmount
 * this component so the content slides in.
 */
const SourcesFileProcessingProgress = ({
    status,
    title = 'Processing file…',
    compact = false,
}) => {
    if (!status || status.kind !== 'processing') {
        return null;
    }

    const progressValue = status.progressPercent;
    const useDeterminate = progressValue != null;
    const circleSize = compact ? 56 : CIRCLE_SIZE;
    const percentLabel = useDeterminate ? `${progressValue}%` : null;

    return (
        <Fade in>
            <Stack
                flex={compact ? undefined : 1}
                alignItems="center"
                justifyContent="center"
                spacing={compact ? 1.5 : 2.5}
                p={compact ? 2 : 4}
                sx={{width: '100%', minWidth: 0}}
            >
                {/* Circular progress with optional percentage inside */}
                <Box sx={{position: 'relative', display: 'inline-flex', flexShrink: 0}}>
                    {/* Track ring */}
                    <CircularProgress
                        variant="determinate"
                        value={100}
                        size={circleSize}
                        thickness={CIRCLE_THICKNESS}
                        sx={(theme) => ({
                            color: theme.palette.action.hover,
                            position: 'absolute',
                            top: 0,
                            left: 0,
                        })}
                    />
                    {/* Active ring */}
                    <CircularProgress
                        variant={useDeterminate ? 'determinate' : 'indeterminate'}
                        value={useDeterminate ? progressValue : undefined}
                        size={circleSize}
                        thickness={CIRCLE_THICKNESS}
                        sx={(theme) => ({color: theme.palette.primary.main})}
                    />
                    {/* Center label */}
                    <Box
                        sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {percentLabel ? (
                            <Typography
                                variant="caption"
                                component="div"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: compact ? '0.7rem' : '0.9rem',
                                    lineHeight: 1,
                                    color: 'text.primary',
                                }}
                            >
                                {percentLabel}
                            </Typography>
                        ) : null}
                    </Box>
                </Box>

                <Stack alignItems="center" spacing={0.5}>
                    <Typography
                        variant={compact ? 'body2' : 'subtitle2'}
                        color="text.primary"
                        textAlign="center"
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant={compact ? 'caption' : 'body2'}
                        color="text.secondary"
                        textAlign="center"
                    >
                        {status.label}
                    </Typography>
                </Stack>
            </Stack>
        </Fade>
    );
};

export default SourcesFileProcessingProgress;
